import { supabase } from './supabase';

interface ErrorLogInsert {
  error_message: string;
  error_stack: string | null;
  component: string | null;
  severity: 'error' | 'warning' | 'fatal';
  user_id: string | null;
  is_anonymous: boolean;
  route: string;
  browser_info: string;
  context: Record<string, unknown>;
  fingerprint: string;
}

function computeFingerprint(message: string, component: string | null): string {
  const normalized = message
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '{uuid}')
    .replace(/\d{4}-\d{2}-\d{2}/g, '{date}')
    .replace(/\d+/g, '{n}')
    .substring(0, 200);
  return `${component || 'unknown'}::${normalized}`;
}

export interface ErrorLogEntry extends ErrorLogInsert {
  id: string;
  created_at: string;
}

interface LogErrorOptions {
  component?: string;
  severity?: 'error' | 'warning' | 'fatal';
  context?: Record<string, unknown>;
  silent?: boolean;
}

// Rate limiting
const RATE_LIMIT = 10;
const RATE_WINDOW_MS = 60_000;
const FLUSH_INTERVAL_MS = 5_000;
const FLUSH_BATCH_SIZE = 5;

let errorCount = 0;
let windowStart = Date.now();
let queue: ErrorLogInsert[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let cachedUserId: string | null = null;
let cachedIsAnon = true;

const SENSITIVE_PATTERNS = [
  /password[=:"]\S+/gi,
  /token[=:"]\S+/gi,
  /bearer\s+\S+/gi,
  /apikey[=:"]\S+/gi,
  /secret[=:"]\S+/gi,
  /authorization[=:"]\S+/gi,
];

function sanitize(text: string): string {
  let result = text;
  for (const pattern of SENSITIVE_PATTERNS) {
    result = result.replace(pattern, '[REDACTED]');
  }
  return result;
}

function sanitizeContext(ctx: Record<string, unknown>): Record<string, unknown> {
  const clean: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(ctx)) {
    if (/password|token|secret|apikey|authorization/i.test(key)) {
      clean[key] = '[REDACTED]';
    } else if (typeof value === 'string') {
      clean[key] = sanitize(value);
    } else {
      clean[key] = value;
    }
  }
  return clean;
}

function isRateLimited(): boolean {
  const now = Date.now();
  if (now - windowStart > RATE_WINDOW_MS) {
    errorCount = 0;
    windowStart = now;
  }
  errorCount++;
  return errorCount > RATE_LIMIT;
}

function normalizeError(error: unknown): { message: string; stack: string | null } {
  if (error instanceof Error) {
    return { message: error.message, stack: error.stack || null };
  }
  if (typeof error === 'string') {
    return { message: error, stack: null };
  }
  try {
    return { message: JSON.stringify(error), stack: null };
  } catch {
    return { message: String(error), stack: null };
  }
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
}

async function flush(): Promise<void> {
  flushTimer = null;
  if (queue.length === 0) return;
  const batch = queue.splice(0);
  try {
    await supabase.from('error_logs').insert(batch);
  } catch {
    // Silent fail — never recurse into logError
  }
}

export function logError(error: unknown, options?: LogErrorOptions): void {
  if (!options?.silent) {
    console.error(error);
  }

  if (isRateLimited()) return;

  const { message, stack } = normalizeError(error);

  queue.push({
    error_message: sanitize(message).slice(0, 2000),
    error_stack: stack ? sanitize(stack).slice(0, 5000) : null,
    component: options?.component ?? null,
    severity: options?.severity ?? 'error',
    user_id: cachedUserId,
    is_anonymous: cachedIsAnon,
    route: typeof window !== 'undefined' ? window.location.pathname : '',
    browser_info: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    context: options?.context ? sanitizeContext(options.context) : {},
    fingerprint: computeFingerprint(sanitize(message).slice(0, 200), options?.component ?? null),
  });

  if (queue.length >= FLUSH_BATCH_SIZE) {
    flush();
  } else {
    scheduleFlush();
  }
}

export function initErrorLogging(): void {
  // Cache user session
  supabase.auth.getSession().then(({ data: { session } }) => {
    cachedUserId = session?.user?.id ?? null;
    cachedIsAnon = !session?.user;
  });
  supabase.auth.onAuthStateChange((_event, session) => {
    cachedUserId = session?.user?.id ?? null;
    cachedIsAnon = !session?.user;
  });

  // Global unhandled errors
  window.addEventListener('error', (event) => {
    logError(event.error ?? event.message, {
      component: 'window.onerror',
      severity: 'fatal',
      context: { filename: event.filename, lineno: event.lineno, colno: event.colno },
      silent: true, // already shown in console by browser
    });
  });

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    // Supabase realtime auto-recovers from expired JWTs by refreshing the session,
    // but the first rejection still bubbles up here. Skip it — it's not actionable.
    const reasonText = typeof event.reason === 'string'
      ? event.reason
      : (event.reason && typeof (event.reason as { message?: unknown }).message === 'string'
          ? (event.reason as { message: string }).message
          : '');
    if (reasonText.includes('InvalidJWTToken')) return;

    logError(event.reason, {
      component: 'unhandledrejection',
      severity: 'fatal',
      silent: true,
    });
  });

  // Flush on page unload
  window.addEventListener('beforeunload', () => {
    if (queue.length > 0) {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/error_logs`;
      const headers = {
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=minimal',
      };
      try {
        fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(queue),
          keepalive: true,
        });
      } catch {
        // Best effort
      }
      queue = [];
    }
  });
}

// Admin functions

export async function getErrorLogs(options?: {
  limit?: number;
  offset?: number;
  severity?: string;
  search?: string;
}): Promise<{ data: ErrorLogEntry[]; count: number }> {
  const limit = options?.limit ?? 50;
  const offset = options?.offset ?? 0;

  let query = supabase
    .from('error_logs')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (options?.severity && options.severity !== 'all') {
    query = query.eq('severity', options.severity);
  }

  if (options?.search) {
    query = query.ilike('error_message', `%${options.search}%`);
  }

  const { data, error, count } = await query;

  if (error) throw error;
  return { data: (data || []) as ErrorLogEntry[], count: count || 0 };
}

export async function purgeErrorLogs(olderThanDays: number): Promise<number> {
  const cutoff = new Date(Date.now() - olderThanDays * 86400000).toISOString();
  const { data, error } = await supabase
    .from('error_logs')
    .delete()
    .lt('created_at', cutoff)
    .select('id');

  if (error) throw error;
  return data?.length || 0;
}

export async function purgeAllErrorLogs(): Promise<number> {
  // `.delete()` without a filter is rejected by supabase-js. Use a tautology
  // that matches every row (created_at is NOT NULL on this table).
  const { data, error } = await supabase
    .from('error_logs')
    .delete()
    .gte('created_at', '1970-01-01')
    .select('id');

  if (error) throw error;
  return data?.length || 0;
}

export async function getGroupedErrors(options?: {
  limit?: number;
  since?: string;
}): Promise<{ fingerprint: string; count: number; last_seen: string; error_message: string; severity: string; component: string }[]> {
  const since = options?.since || new Date(Date.now() - 7 * 86400000).toISOString();

  const { data, error } = await supabase.rpc('get_grouped_errors', {
    since_date: since,
    max_results: options?.limit || 50,
  });

  if (error) {
    // Fallback to ungrouped
    const { data: fallback } = await supabase
      .from('error_logs')
      .select('*')
      .gte('created_at', since)
      .order('created_at', { ascending: false })
      .limit(options?.limit || 50);
    return (fallback || []).map((e: any) => ({
      fingerprint: e.fingerprint || e.error_message?.substring(0, 80),
      count: 1,
      last_seen: e.created_at,
      error_message: e.error_message,
      severity: e.severity,
      component: e.component,
    }));
  }

  return data || [];
}

export async function getRecentErrorCount(): Promise<number> {
  const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const { count, error } = await supabase
    .from('error_logs')
    .select('*', { count: 'exact', head: true })
    .gte('created_at', since);

  if (error) return 0;
  return count || 0;
}
